/**
 * useTenantId Hook — the REACTIVE wrapper over src/lib/tenant.ts.
 *
 * Wave 2: the non-React core (getTenantIdSync, tenantIdReadUnion, the
 * alias map) moved to src/lib/tenant.ts so services no longer import from
 * the hooks layer. This module keeps the auth-state listener that syncs
 * the token claim into localStorage, and re-exports the core accessors for
 * existing hook/component importers.
 *
 * Resolution order:
 * 1. Firebase Auth token claims (most secure, set by Cloud Function)
 * 2. localStorage (fallback for PIN-based auth sessions)
 * 3. Default tenant ID (for unauthenticated access)
 *
 * @example
 * const tenantId = useTenantId();
 * const q = query(collection(db, 'jobs'), where('tenantId', '==', tenantId));
 */

import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebase';
import { cacheIsAdmin } from '@/utils/access';
import { DEFAULT_TENANT_ID, getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';

export { DEFAULT_TENANT_ID, getTenantIdSync, tenantIdReadUnion };

/**
 * Get the current tenant ID from various sources
 */
export function useTenantId(): string {
  const [tenantId, setTenantId] = useState<string>(() => {
    // Initial value from localStorage (synchronous)
    return getTenantIdSync();
  });

  useEffect(() => {
    // Listen for auth state changes to get tenantId from token
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Get ID token result to access custom claims
          const tokenResult = await user.getIdTokenResult();
          const tokenTenantId = tokenResult.claims.tenantId as string;

          if (tokenTenantId) {
            setTenantId(tokenTenantId);
            // Sync to localStorage for consistency
            localStorage.setItem('tenant_id', tokenTenantId);
          }
          // Cache the isAdmin claim so the sync isAdminSync() gate (destructive
          // tools) has a fresh value without an async token read.
          cacheIsAdmin(tokenResult.claims.isAdmin === true);
        } catch (error) {
          console.warn('[useTenantId] Failed to get token claims:', error);
        }
      } else {
        // No auth user - fall back to localStorage
        setTenantId(getTenantIdSync());
        cacheIsAdmin(false);
      }
    });

    return unsubscribe;
  }, []);

  return tenantId;
}

export default useTenantId;
